/**
 * WHAT SHE TYPES TO SIGN IN, READ BY THE WORKER'S OWN RULE (home #11). A copy of
 * `normaliseIdentifier` in `api/src/lib/identifier.ts`, line for line, because the screens import
 * nothing from the Worker; `api/test/identifier.test.ts` runs both over the same entries, so the two
 * can never disagree about one.
 *
 * Where the host can text, the sign-in field is a text field and the browser checks nothing, so the
 * page reads what she typed here first, and says what to fix for an entry that is neither an email
 * address nor a mobile number, rather than sending it to be refused. Nothing typed is compared with
 * anything on file, so the answer says nothing of whether anybody is a client (CLAUDE.md binding
 * rule 3).
 *
 * A string with an `@` in it is an address: trimmed, lowercased, its domain in ASCII, at most 254
 * characters, and valid by the rule `input type=email` enforces. Anything else is a number, read
 * to E.164: ten digits, eleven with the country code in front, or E.164 itself, written with
 * spaces, dots, dashes and brackets as people write them.
 */

/** How a code travels, in the Worker's words: `email`, or `sms`. */
export type IdentifierChannel = 'email' | 'sms';

export interface Identifier {
  readonly channel: IdentifierChannel;
  /** The address, lowercased, or the number in E.164. */
  readonly value: string;
}

/** The WHATWG valid email address, as `input type=email` checks it. */
const VALID_EMAIL =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** The longest address SMTP can carry in a path. */
const MAX_EMAIL_LENGTH = 254;

/** E.164: a plus, then eight to fifteen digits, the first of them never zero. */
const E164 = /^\+[1-9][0-9]{7,14}$/;

/**
 * A domain as it may be typed: letters in any script, and in ASCII only letters, digits, dots and
 * hyphens.
 */
const TYPED_DOMAIN = /^[a-z0-9.\u0080-\u{10ffff}-]+$/u;

/**
 * A domain in the ASCII form the browser checks `input type=email` against, or null where it is
 * not a domain at all.
 */
function asciiDomain(domain: string): string | null {
  if (!/[^\x00-\x7f]/.test(domain)) return domain;
  if (!TYPED_DOMAIN.test(domain)) return null;
  try {
    return new URL(`http://${domain}`).hostname;
  } catch {
    return null;
  }
}

/** A number in E.164 as she reads one: "(727) 555-0142" for a US number, else as it is. */
export function numberShown(e164: string): string {
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return us === null ? e164 : `(${us[1]}) ${us[2]}-${us[3]}`;
}

/**
 * What she signed in with as a screen names it: her address as it is, or her number as she reads
 * one (home #11).
 */
export function signedInShown(signedInWith: {
  readonly channel: 'email' | 'text';
  readonly value: string;
}): string {
  return signedInWith.channel === 'text' ? numberShown(signedInWith.value) : signedInWith.value;
}

/** The address or number she typed, as the Worker reads it, or null when it is neither. */
export function normaliseIdentifier(raw: string): Identifier | null {
  if (raw.includes('@')) {
    const typed = raw.trim().toLowerCase();
    const at = typed.lastIndexOf('@');
    const domain = asciiDomain(typed.slice(at + 1));
    if (domain === null) return null;
    const value = `${typed.slice(0, at)}@${domain}`;
    if (value.length > MAX_EMAIL_LENGTH || !VALID_EMAIL.test(value)) return null;
    return { channel: 'email', value };
  }

  const compact = raw.replace(/[\s().-]/g, '');
  if (E164.test(compact)) return { channel: 'sms', value: compact };
  // A US number as people write one: ten digits, or eleven with the country code in front.
  if (/^[0-9]{10}$/.test(compact)) return { channel: 'sms', value: `+1${compact}` };
  if (/^1[0-9]{10}$/.test(compact)) return { channel: 'sms', value: `+${compact}` };
  return null;
}
